import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { DeleteConfirmationModal, ErrorModal, SuccessModal } from "@components";
import { Button, Table } from "@pentabd/ui";

import { TableColumn } from "./ProcurementDetailsTableColumn";
import { IconPlus } from "@pentabd/icons";
import { Link, useNavigate } from "react-router-dom";
import { ROUTES } from "@constants/routes";
import useActionCreator from "@redux/hooks/useActionCreator";
import { usePackageList } from "@hooks/procurements/package/usePackages";
import { PackagesResponseType } from "@type/procurements/package";
import { Pagination } from "@type/procurements/pagination";
import useCustomSelector from "@redux/hooks/useCustomSelector";
import { getAccess } from "@helpers/permissionController";
import { useUser } from "@hooks/useUser";
import { TablePagination } from "@helpers/tablePagination";

type ProcurementDetailsTablePropType = {
  id: number | string;
  remainingAppTotalBudget: number;
};

export const ProcurementDetailsTable = ({
  id,
  remainingAppTotalBudget,
}: ProcurementDetailsTablePropType) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [itemId, setItemId] = useState<number>();
  const [itemName, setItemName] = useState<string>("");

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { packageDeleteActionHandler } = useActionCreator();

  const { user } = useUser();
  const { realm_roles: roles } = user || {};

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const { handleQuery, packageState } = usePackageList(id);

  const { getData } = packageState;

  const { data, pagination } = getData || ({} as PackagesResponseType);
  const { page } = pagination || ({} as Pagination);

  const { deleteSuccess, deleteError } = useCustomSelector(
    (store) => store.procurements.packageState,
  );

  const createSuccessAction = () => {
    setIsSuccessModalOpen(false);
    handleQuery("page", page);
    navigate(`${ROUTES.PROCUREMENT.ANNUAL_PROCUREMENT_PLAN}`);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const closeConfirmationModel = () => setIsConfirmationModalOpen(false);

  const openConfirmationModel = (itemName: string, id: string | number) => {
    setItemId(id as number);
    setItemName(itemName);
    setIsConfirmationModalOpen(true);
  };

  const deleteAction = () => {
    closeConfirmationModel();
    packageDeleteActionHandler(itemId as number);
  };

  useEffect(() => {
    if (deleteSuccess) {
      setIsSuccessModalOpen(true);
    } else if (!!deleteError) {
      setIsErrorModalOpen(true);
    }
  }, [deleteSuccess, deleteError]);

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
      setIsConfirmationModalOpen(false);
    };
    return reset();
  }, []);

  return (
    <>
      {/* modals  */}
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
        />
      )}
      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
          subtitle={
            !!deleteError
              ? deleteError?.response?.data?.message
              : "Something went wrong"
          }
        />
      )}
      {/* modal ends */}

      {/* delete confirmation modal  */}
      {isConfirmationModalOpen && (
        <DeleteConfirmationModal
          subtitle={itemName}
          isModalOpen={isConfirmationModalOpen}
          onClose={closeConfirmationModel}
          action={deleteAction}
        />
      )}

      <Table
        header={{
          title: t("APP_DETAILS.TABLE.LABEL"),
          buttons: [
            getAccess(roles, "PROCUREMENT_HR") ? (
              <Link to={`${ROUTES.CREATE}`}>
                <Button
                  fill="fill"
                  size="sm"
                  type="primary"
                  htmlType="button"
                  disabled={remainingAppTotalBudget ? false : true}
                >
                  <IconPlus size="20" fill="white" />
                  {t("APP_DETAILS.TABLE.BUTTON")}
                </Button>
              </Link>
            ) : (
              <></>
            ),
          ],
        }}
        columns={TableColumn(
          t,
          navigate,
          roles,
          openConfirmationModel,
          getAccess,
        )}
        // edit={{
        //   customIcon: <IconEdit01 size={24} fill="subtitle1" />,
        //   onClick: (id) => {
        //     navigate(`${id}`);
        //   },
        // }}
        // remove={
        //   getAccess(roles, "PROCUREMENT_HR")
        //     ? {
        //         customIcon: <IconTrash01 size={24} fill="danger" />,
        //         onClick: (packageId) => {
        //           packageDeleteActionHandler(packageId);
        //         },
        //       }
        //     : {
        //         onClick: (id) => {},
        //       }
        // }
        rows={data || []}
        pagination={TablePagination(pagination, handleQuery)}
      />
    </>
  );
};
